<template>
    <div v-if="pageData.data" class="palestine-prisoners">
       <div class="zone-title zone-title--bg banded landing__zone-title--short">
            <div class="gradient">
                <div class="container-title container-headline container-headline--no-image">
                    <h1>Palestinian Prisoner Payments</h1>
                </div>
            </div>
        </div>
         <section class="top-padding-standard bottom-padding-standard background-grey tier">
            <div class="inner cols">
                <div class="col col-50">
                    <div class="gutters">
                        <h4>About this Resource</h4>
                        <p>This resource is part of the  Breaking the Israel-Palestine Status Quo: A Rights-Based Approach Project.</p>
                    </div>
                </div>
                <div class="col col-50">
                    <div class="gutters">
                        <a class="anchor button-secondary theme-light size-regular icon-position-right button carnegie-button button" href="https://carnegieendowment.org/projects/breaking-the-israel-palestine-status-quo-a-rights-based-approach?lang=en">
                            <span class="label direction-ltr typography label">More about the project</span>
                        </a>
                    </div>
                </div>
            </div>
        </section>
        <div class="section background white intro">
            <div class="section foreground">
                    <p>Since 2014, U.S. and Israeli policymakers have been calling on the Palestinian leadership to end welfare payments to prisoners in Israeli jails and to the families of prisoners and those killed by Israelis during political violence. Dubbing these payments “pay for slay,” critics claim that they incentivize militant resistance. However, Palestinians and supporters view them as a necessary social safety net in the face of brutal and oppressive living conditions under Israeli military occupation. The reality is that comparatively high numbers of Palestinians are imprisoned, and almost half of the Palestinian population is in need of humanitarian assistance.<sup>1</sup></p>
                    <div class="center gutter-bottom"><a class="button-big teal" href="https://ceipfiles.s3.amazonaws.com/pdf/PalestinianPrisonerPayments_FactSheet_final.pdf">Download Full Text (PDF)</a></div>
                    <div class="video-block" ref="videoParent">
                        <client-only>
                            <div class="videoEmbed"><iframe width="560" height="315" src="https://www.youtube.com/embed/Wroaqt6IbpY?si=cSECbwa4kN9qjWb_" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>
                        </client-only>
                    </div>
            </div>
        </div>
        <div class="section foreground description">
            <h3 class="gutter-top__double">Israel’s Military Court System</h3> 
            <p>Israel broadly defines what constitutes a security offense in the occupied territories. And as such, many Palestinians are prosecuted in military courts and face lengthy penalties for even nonviolent activities. </p>
            <div class="cols">
                <div class="col col-50">
                    <div class="gutter-right">
                        <div class="large-number">99%</div>
                        <div class="explanation">conviction rate for Palestinians processed through Israeli courts<sup>2</sup></div>
                    </div>
                </div>
                <div class="col col-50">
                    <div class="large-number">3%</div>
                    <div class="explanation">of cases where Israelis are accused of committing crimes against Palestinians led to convictions between 2005 and 2017 </div>
                </div>
            </div>
            <h4>Types of Security Offenses<sup>3</sup></h4>
            <div class="cols gutter-top circle-numbers">
                <div class="col col-05"><span class="circle">1</span></div>
                <div class="col col-95">
                    <div class="text">Participating, without a permit, in a demonstration of more than ten people that could be construed as political</div>
                </div>
            </div>
            <div class="cols gutter-top circle-numbers">
                <div class="col col-05"><span class="circle">2</span></div>
                <div class="col col-95">
                    <div class="text">Waving flags or taking part in vigils without approval</div>
                </div>
            </div>
            <div class="cols gutter-top circle-numbers">
                <div class="col col-05"><span class="circle">3</span></div>
                <div class="col col-95">
                    <div class="text">Belonging to an organization that Israel designates as illegal, including groups that make up the Palestine Liberation Organization (PLO)</div>
                </div>
            </div>
            <div class="cols gutter-top circle-numbers">
                <div class="col col-05"><span class="circle">4</span></div>
                <div class="col col-95">
                    <div class="text">Posting social media messages critical of Israeli forces or the occupation</div>
                </div>
            </div>
            <h4>Indictments of Palestinians, 2017<sup>4</sup></h4>
            <span class="contentImg wide gutter-top">
                <img src="https://ceipimg.s3.amazonaws.com/static/breakingtheisraelpalestinestatusquo/Indictments_web-01.png" alt="Graph of indictments of Palestinians, 2017" border="0"/>
            </span>
            <h3>Pervasive Detention</h3>
            <p>The pay-for-slay framing obscures the realities of the occupation for Palestinians. Political violence causes considerable harm to both Palestinians and Israelis, but Palestinians pay a disproportionately high cost in terms of detention. They also account for a majority of the conflict-related casualties (87 percent between 2000 and 2014).<sup>5</sup></p>

            <div class="cols">
                <div class="col col-33">
                    <div class="gutter-right">
                        <div class="large-number">1 million</div>
                        <div class="explanation">Palestinians detained by Israel since 1948<sup>6</sup></div>
                    </div>
                </div>
                <div class="col col-33">
                    <div class="gutter-right">
                        <div class="large-number">40%</div>
                        <div class="explanation">of Palestinian men detained since 1967</div>
                    </div>
                </div>
                <div class="col col-33">
                    <div class="gutter-right">
                        <div class="large-number">70%</div>
                        <div class="explanation">of Palestinian families with at least one relative detained
                        </div>
                    </div>
                </div>
            </div>
            <h4>Detention of Children</h4>
            <div class="cols">
                <div class="col col-30 align-right">
                    <div class="gutter-right">
                        <div class="large-number gutter-top">500–700 </div>
                        <div class="explanation">children detained each year<sup>7</sup></div>
                        <div class="large-number gutter-top">15,000 </div>
                        <div class="explanation">children detained between 2000 and 2017<sup>8</sup>
                        </div>
                    </div>
                </div>
                <div class="col col-70"> <span class="contentImg wide gutter-top"><img src="https://ceipimg.s3.amazonaws.com/static/breakingtheisraelpalestinestatusquo/ChildDetention_web-01.png" alt="Graph of detention of children" border="0"/></span></div>
            </div>
            <div class="gutter-top">
                <div class="handcuffs"><span class="description">In 2019, <span class="teal">73 percent of children experienced physical violence</span> following arrest and <span class="teal">88 percent children were interrogated</span> without a family member or lawyer present<sup>9</sup>
                    </span>
                </div>
            </div>
            <h3 class="gutter-top__double">Palestinian Prisoners in the Context of Occupation</h3>
            <p>While Israeli settlers in the occupied territories are subject to civilian courts and receive support from tax-exempt U.S. organizations, Palestinians and their families face additional disproportionate punitive measures for crimes committed or suspected to have been committed, including the demolition of family homes.</p>
            <h4>Punitive Home Demolitions Since 2014<sup>10</sup></h4>
            <span class="contentImg wide gutter-top demolitions">
                <img src="https://ceipimg.s3.amazonaws.com/static/breakingtheisraelpalestinestatusquo/Homelessness_Demolitions_web-01.png" alt="Graph of home demolitions" border="0"/></span>
            <p>Note: Punitive demolitions were paused between 2005 and 2014, except two sealings and one demolition in East Jerusalem, after an Israeli Military Committee found the policy was not an effective deterrent.<sup>11</sup></p>
            <h4>Punitive Home Demolitions During the Al-Aqsa intifada, 2000–2004<sup>12</sup></h4>
            <div class="cols gutter-top cols--equal-heights">
                <div class="col col-20">
                    <div class="piechart"></div>
                </div>
                <div class="col col-10">
                    <div class="large-number centered">47%</div>
                </div>
                <div class="col col-70">
                    <div class="gutter-left centered">of homes demolished were never home to anyone suspected of involvement in attacking Israelis
                    </div>
                </div>
            </div>
            <h3 class="gutter-top__double">Prisoner Payments as a Safety Net</h3>
            <p>Support for the payments is high at 91 percent among Palestinians.<sup>13</sup> They see the payments as a necessary counterbalance to the indiscriminate application of Israeli military law and the challenges of living under Israeli occupation.</p>
            <div class="cols">
                <div class="col col-50">
                    <div class="gutter-right teal gutter-left">Almost half of the Palestinian population is in need of humanitarian aid<sup>14</sup></div>
                </div>
                <div class="col col-50">
                    <div class="large-number">28.9% <span class="inline-text">Poverty rate<sup>15</sup></span>
                    </div>
                </div>
            </div>
            <h4>Payments to Families of Those Killed by Israel Include<sup>16</sup></h4>
            <div class="cols gutter-top payments-numbers">
                <div class="col col-33">
                    <div class="gutter-right">
                        <div class="large-number">$350 </div>
                        <div class="explanation">monthly stipend per family </div>
                    </div>
                </div>
                <div class="col col-33">
                    <div class="gutter-right">
                        <div class="large-number">+$100</div>
                        <div class="explanation">if they were married</div>
                    </div>
                </div>
                <div class="col col-33">
                    <div class="gutter-right">
                        <div class="large-number">+$50 </div>
                        <div class="explanation">for each child
                        </div>
                    </div>
                </div>
            </div>
            <h4>Number of Prisoners Receiving Payments in 2017<sup>17</sup></h4>
            <div class="cols gutter-top prisoners-list">
                <div class="col col-50 prisoners center">
                    <div class="large-number"><span>13,000<div class="explanation">Prisoners</div></span></div>
                </div>
                <div class="col col-50 families center">
                    <div class="large-number"> <span>33,700<div class="explanation">Families</div></span></div>
                </div>
            </div>
            <div class="clearfix"> </div>
            <h3 class="overcome-padding">U.S. Policy and Assistance</h3>
            <p>In 2018, Congress passed the Taylor Force Act—named after a graduate student and U.S. Army veteran who was killed by a Palestinian inside Israel. The law prohibits certain U.S. economic assistance to the Palestinian Authority (PA) until the welfare payment system for prisoners and families stop and is replaced by a purely needs-based system. While the PA and PLO have signalled a willingness to reform, a universal needs-based welfare system may not be financially possible without international donor funding.
            </p>
            <p>In the short term, President Joe Biden’s administration can legally provide many kinds of aid to the Palestinians, including humanitarian aid, security assistance, and voluntary contributions to the United Nations Relief and Works Agency. The United States can also fund specific programs exempted by the Taylor Force Act, including the East Jerusalem Hospital Network and wastewater projects. </p>
        </div>
        <div class="section foreground">
            <h3 class="notes">Notes </h3>
            <p><sup>1</sup> &ldquo;Report to the Ad Hoc Liaison Committee, Office of the United Nations Special Coordinator for the Middle East Peace Process,&rdquo; February 23, 2021, <a href="https://unsco.unmissions.org/sites/default/files/unsco_report_to_the_ahlc_-_february_2021_0.pdf">https://unsco.unmissions.org/sites/default/files/unsco_report_to_the_ahlc_-_february_2021_0.pdf</a>.</p>
            <p><sup>2</sup> Jaclynn Ashly, &ldquo;Israel&rsquo;s Military Courts &lsquo;Humiliating Charade&rsquo; for Palestinians,&rdquo; Al Jazeera, February 26, 2018, <a href="https://www.aljazeera.com/features/2018/2/26/israels-military-courts-humiliating-charade-for-palestinians">https://www.aljazeera.com/features/2018/2/26/israels-military-courts-humiliating-charade-for-palestinians</a>    </p>
            <p><sup>3</sup> Human Rights Watch, &ldquo;Israel/West Bank: Grant Palestinians Equal Rights,&rdquo; December 17, 2019, <a href="https://www.hrw.org/news/2019/12/17/israel/west-bank-grant-palestinians-equal-rights">https://www.hrw.org/news/2019/12/17/israel/west-bank-grant-palestinians-equal-rights</a>.</p>
            <p><sup>4</sup> Amira Hass, &ldquo;In Three Years, Israeli Military Courts Have Fined Palestinians $16 Million,&rdquo; Haaretz<em>, </em>January 15, 2019, <a href="https://www.haaretz.com/israel-news/.premium-in-three-years-israeli-military-courts-have-fined-palestinians-16-million-1.6830009">https://www.haaretz.com/israel-news/.premium-in-three-years-israeli-military-courts-have-fined-palestinians-16-million-1.6830009</a>.</p>
            <p><sup>5</sup> Max Fisher, &ldquo;This Chart Shows Every Person Killed in the Israel-Palestine Conflict Since 2000,&rdquo; Vox, July 14, 2014, <a href="https://www.vox.com/2014/7/14/5898581/chart-israel-palestine-conflict-deaths">https://www.vox.com/2014/7/14/5898581/chart-israel-palestine-conflict-deaths</a>.</p>
            <p><sup>6</sup> Commission of Detainees and Ex-Detainees Affairs, Palestinian Prisoners Club and Palestinian Central Bureau of Statistics, &ldquo;Press Release on the Eve of Palestinian Prisoners Day,&rdquo; April 17, <a href="http://www.pcbs.gov.ps/post.aspx?lang=en&amp;ItemID=1905">http://www.pcbs.gov.ps/post.aspx?lang=en&amp;ItemID=1905</a>.</p>
            <p><sup>7</sup> Defense for Children International&mdash;Palestine, "Military Detention," <a href="https://www.dci-palestine.org/issues_military_detention">https://www.dci-palestine.org/issues_military_detention</a>. </p>
            <p><sup>8</sup> Commission of Detainees and Ex-Detainees Affairs, Palestinian Prisoners Club and Palestinian Central Bureau of Statistics, &ldquo;Press Release on the Eve of Palestinian Prisoners Day.&rdquo; </p>
            <p><sup>9</sup> United Nations Children&rsquo;s Fund (UNICEF), &ldquo;UNICEF Children and Armed Conflict Bulletin 2019,&rdquo; <a href="https://www.unicef.org/sop/media/1491/file/nnual%20CAAC%20Bulletin%202019.pdf">https://www.unicef.org/sop/media/1491/file/nnual%20CAAC%20Bulletin%202019.pdf</a>.</p>
            <p><sup>10</sup> B&rsquo;Tselem, &ldquo;House Demolitions: Demolition of Houses as Punishment,&rdquo; updated March 30, 2021, <a href="https://statistics.btselem.org/en/demolitions/demolition-as-punishment?tab=overview&amp;structureSensor=%22true%22&amp;demoScopeSensor=%22false%22">https://statistics.btselem.org/en/demolitions/demolition-as-punishment?tab=overview&amp;structureSensor=%22true%22&amp;demoScopeSensor=%22false%22</a>.</p>
            <p><sup>11</sup> B&rsquo;Tselem, &ldquo;Home Demolition as Collective Punishment,&rdquo; November 11, 2017, <a href="https://www.btselem.org/punitive_demolitions#:~:text=The%20policy%20of%20punitive%20house,Israeli%20civilians%20or%20security%20forces">https://www.btselem.org/punitive_demolitions#:~:text=The%20policy%20of%20punitive%20house,Israeli%20civilians%20or%20security%20forces</a>.</p>
            <p><sup>12</sup> B&rsquo;Tselem, &ldquo;Through No Fault of Their Own: Israel&rsquo;s Punitive House Demolitions in the al-Aqsa Intifada,&rdquo; November 2004, <a href="https://www.btselem.org/publications/summaries/200411_punitive_house_demolitions">https://www.btselem.org/publications/summaries/200411_punitive_house_demolitions</a>.</p>
            <p><sup>13</sup> &ldquo;Public Opinion Poll #64,&rdquo; Palestinian Center for Policy and Survey Research<em>, </em>July 13, 2017, <a href="https://www.pcpsr.org/en/node/694">https://www.pcpsr.org/en/node/694</a>. </p>
            <p><sup>14</sup> &ldquo;Report to the Ad Hoc Liaison Committee, Office of the United Nations Special Coordinator for the Middle East Peace Process,&rdquo; February 23, 2021, <a href="https://unsco.unmissions.org/sites/default/files/unsco_report_to_the_ahlc_-_february_2021_0.pdf">https://unsco.unmissions.org/sites/default/files/unsco_report_to_the_ahlc_-_february_2021_0.pdf</a>.</p>
            <p><sup>15</sup> World Bank, &ldquo;Economic Monitoring Report to the Ad Hoc Liaison Committee,&rdquo; February 23, 2021, <a href="https://www.un.org/unispal/document/world-bank-report-to-ad-hoc-liaison-committee-ahlc-23-february-2021/">https://www.un.org/unispal/document/world-bank-report-to-ad-hoc-liaison-committee-ahlc-23-february-2021/</a>.</p>
            <p><sup>16</sup> Mohammed Daraghmeh and Josef Federman, &ldquo;Israel Puts Palestinians on Defensive Over &lsquo;Martyrs&rsquo; Fund,&rsquo;&rdquo; AP News<em>, </em>July 11, 2016, <a href="https://apnews.com/article/11f6ce1ed78943a0afc947e81334cef9">https://apnews.com/article/11f6ce1ed78943a0afc947e81334cef9</a>.</p>
            <p><sup>17</sup> Glenn Kessler, &ldquo;Does the Palestinian Authority Pay $350 Million a Year to &lsquo;Terrorists and Their Families&rsquo;?&rdquo; <em class="roman-italic-book">Washington Post</em>, March 14, 2018, <a href="https://www.washingtonpost.com/news/fact-checker/wp/2018/03/14/does-the-palestinian-authority-pay-350-million-a-year-to-terrorists-and-their-families/">https://www.washingtonpost.com/news/fact-checker/wp/2018/03/14/does-the-palestinian-authority-pay-350-million-a-year-to-terrorists-and-their-families/</a>.</p>
        </div>
        <!-- commenting something out -->
    </div>
</template>

<script setup>
import FeatureFragment from '@fragments/pages/FeatureFragment.ts';

const pageData = await usePageData(FeatureFragment, 'features');
</script>

<style lang="scss" scoped>
$gutter: 30px;
$site-width: 1000px;

$gold: #a28556;
$black: #000;
$white: #fff;
$teal: #00a79d;
$dark-gray-new: #6e6e71;
$light-gray-new: #e6e7e8;
$dark-blue: #00365e;
$yellow: #fbb040;
$red: #ef4136;
$carnegie-blue: #003a63;
$tertiary: $dark-gray-new; //$gray-dark;

$roman-normal-book: 'Apercu-Medium', Arial, sans-serif;
$roman-italic-book: 'Apercu-Medium-Italic', Arial, sans-serif;
$roman-normal-bold: 'Apercu-Bold', 'Arial Black', sans-serif;
$roman-italic-bold: 'Apercu-Bold-Italic', 'Arial Black', sans-serif;

%bold-sans-text {
    font-family: $roman-normal-bold;
    font-weight: 700;
}

%italic-sans-text {
    font-family: $roman-normal-book;
    font-style: italic;
}

.palestine-prisoners {

    //layout basics
    .foreground {
        position: relative;
        margin: 0 auto;
        box-sizing: content-box;
        max-width: $site-width;
        padding-left: calc($gutter / 2);
        padding-right: calc($gutter / 2);
    }

    .section {
        display: block;
        clear: both;
        margin-top: $gutter;
        margin-bottom: $gutter;
    }

    .cols {
        clear: both;
        //use a .no-gutters class to undo these default styles
        margin-left: calc($gutter / 2) * -1;
        margin-right: calc($gutter / 2) * -1;

        &:before,
        &:after {
            content: ' ';
            display: table;
            height: 0;
            clear: both;
            font-size: 1px;
            line-height: 0px;
            visibility: hidden;
        }
    }

    .cols--equal-heights {
        flex-wrap: wrap;

        &,
        >.col {
            display: flex;

            // @include bp(mobile) {
            //     display: block;
            // }
        }

        >.col {
            flex-direction: column;
        }

        .banded {
            flex: 1 0 auto;
        }
    }

    .gutter-top {margin-top: 15px}

    .cols {
        clear: both;
    }

    .col {
        float: left;

        .gutters {
            padding-left: calc($gutter / 2);
            padding-right: calc($gutter / 2);
        }

        // @include bp(mobile) {
        //     float: none;
        //     width: 100% !important;

        //     > .gutters {
        //         margin-left: 0;
        //         margin-right: 0;
        //     }
        // }
    }

    .col-80 {
        width: 80%;
    }

    .col-70 {
        width: 70%;
    }

    //two across
    .col-50 {
        width: 50%;
    }

    .col-33 {
        width: 33%;
    }

    .col-30 {
        width: 30%;
    }

    //four across
    .col-25 {
        width: 25%;
    }

    //five across
    .col-20 {
        width: 20%;
    }

    .padded {
        padding: $gutter;
    }

    .gutter-right {
        margin-right: 30px;
    }

    .gutter-left {
        margin-left: 30px;
    }

    :deep(.accordion-content) {
        display: none;

        &.active {
            display: block;
            margin-top: 3rem;
        }
    }

    .section {
        font-family: $roman-normal-book;
        margin-bottom: 0;
        margin-top: 0;
        z-index: 3;
    }

    h1,
    h2,
    h3 {
        margin-bottom: 1.4rem;
        &:not(:first-child) {
            margin-top: 4rem;
        }

        @include media-query(phone) {
            margin-bottom: 0.8rem;
        }
    }

    h4,
    h5 {
        margin-bottom: 0.8rem;
        margin-top: 3rem;
    }

    p {
        margin-bottom: 1em;
        a {
            text-decoration-line: underline;
            text-decoration-thickness: .0833333333em;
            -webkit-text-decoration-skip: ink;
            overflow-wrap: break-word;
            text-decoration-color: var(--color-anchor-underline);
            text-decoration-skip-ink: auto;
            text-underline-offset: .25em;
        }
    }
    sup {
        vertical-align: super;
        font-size: smaller;
    }

    .videoEmbed {
        position: relative;
        padding-bottom: 56.25%;
        height: 0;
        overflow: hidden;
        max-width: 100%;
        margin-bottom: 30px;

        .embed-floated & {
            margin-bottom: 0;
        }

        iframe,
        .video-embed iframe,
        .video-embed object,
        .video-embed embed {
            position: absolute;
            top: 0;
            left: 0;
            width: 100% !important;
            height: 100% !important;
        }
    }

    .zone-title {
        background-attachment: fixed;
        background-color: $white;
        background-image: url(https://carnegie-production-assets.s3.amazonaws.com/static/media/images/hdr-breakingIsraelPalestineStatusQuo.jpg);
        background-position: right top;
        background-size: 120% auto;
        background-repeat: no-repeat;
        height: 300px;
        text-align: left;

        .container-title {
            padding-top: 140px;
            margin: 0 0 0 4%;
            width: 55%;
        }

        h1 {
            color: $white;
            display: inline-block;
            font-family: $roman-normal-bold;
            font-size: 4rem;
            margin: $gutter 0;
            padding: calc($gutter / 5) 20px 12px 20px;
        }
    }

    .background {
        padding: calc($gutter * 2) 0;
        position: relative;
    }

    .intro {
        border-top: none;
        display: block;
        position: relative;
        z-index: 3;

        .foreground {
            margin-top: $gutter;
            margin-bottom: $gutter;
        }

        p {
            margin-bottom: 1.3rem;
        }
        
    }

    .button-secondary {
        border: 1px solid rgb(var(--color-rgb-teal-default));
        border-radius: 5px;
        color: rgba(var(--color-rgb-blue-default),.7);
        gap: .8rem;
        text-decoration: none;
        padding: 1.6rem 2.6rem 1.8rem;
        transition-duration: 125ms;
        align-items: center;
        display: inline-flex;
        flex-wrap: wrap;
        text-align: center;
        transition-property: background-color,color;
        transition-timing-function: ease-in;
    }

    a.button-big {
        gap: 0.8rem;
        color: rgba(var(--color-rgb-blanc-default), 0.8);
        background-color: rgb(var(--color-rgb-teal-default));
        border-radius: 5px;
        padding: 1.6rem 2.6rem 1.8rem;
        margin: 2rem 0;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        text-align: center;
    }

    .tier {
        background: rgb(var(--color-rgb-offblanc-default));
        display: block;
        padding-bottom: 6.4rem;
        padding-top: 6.4rem;

        .inner {
            margin: 0 auto;
            max-width: 144rem;
            padding-left: var(--page-margin);
            padding-right: var(--page-margin);
            width: 100%;

            h4 {
                font-family: GT Sectra,Georgia,serif;
                font-size: 2.4rem;
                font-weight: 350;
                line-height: 1.1666666667;
            }

            p {
                font-family: GT Sectra,Georgia,serif;
                font-size: 1.6rem;
                font-weight: 350;
                line-height: 1.5;
            }
        }
    }

    .headline {
        .tag {
            background-color: $black;
            border: none;
            clear: both;
            display: inline-block;
        }
        span {
            display: block;
            margin-top: calc($gutter / 1.5);
        }
    }

    .description {
        font-family: $roman-normal-book;

        h4 {margin-top: $gutter*2.5; margin-bottom: $gutter*1.5}
        .button-big.teal {background-color: $teal;}
        .handcuffs {
            background-image: url(https://ceipimg.s3.amazonaws.com/static/breakingtheisraelpalestinestatusquo/Handcuffs_web-01.png);
            background-position: left top;
            background-repeat: no-repeat;
            background-size: contain;
            margin-left: $gutter*2;
            margin-right: $gutter*2;

            .description {display: block; margin-left: $gutter*3;}
            .teal {color: $teal;}
        }

        h3.overcome-padding {margin-top: $gutter*5;}
        a {word-wrap: break-word}
        p {
            color: #000;
            font-size: 1.7rem;
            line-height: 1.7em;
            margin-bottom: 5%;
        }
        .explanation, .text {font-size: 1.7rem;}
    }
    .cols {
        margin-left: 0;
        margin-right: 0;

        .col-05 {width: 5%;}
        .col-95 {width: 95%;}
        
        .teal {color: $teal;}

        .large-number {
            color: #1b107c; 
            font-size: 3rem; 
            font-family: $roman-normal-bold; 
            margin-bottom: calc($gutter / 3);

            span.inline-text {font-size: 1.3rem;}
        }

        .circle {
            border-radius: 50%; 
            display: inline-block;
            width: 25px;
            line-height: 25px;
            font-size: 15px;
            background: #1b107c;
            color: $white;
            text-align: center;
            vertical-align: bottom;
        }

        .piechart {
            background-image: url('https://ceipimg.s3.amazonaws.com/static/breakingtheisraelpalestinestatusquo/Al-Asqa Intifada_pie_web-01.png');
            background-position: left center;
            background-repeat: no-repeat;
            background-size: contain;
            height: 200px;
        }
        .large-number.centered {padding-top: 50%}
        .gutter-left.centered {padding-top: 5%}

        .prisoners {
            .large-number {
                background-image: url('https://ceipimg.s3.amazonaws.com/static/breakingtheisraelpalestinestatusquo/Prisoners_web-01.png');
                background-position: center top;
                background-repeat: no-repeat;
                background-size: contain;
                height: 100px;

                span {display: block; padding-top: $gutter*4}
                .explanation {color: $black; font-size: 1.3rem; font-family: $roman-normal-book; margin-top: calc($gutter / 3)}
            }
        }

        .families {
            .large-number {
                background-image: url('https://ceipimg.s3.amazonaws.com/static/breakingtheisraelpalestinestatusquo/Families_web-01.png');
                background-position: center top;
                background-repeat: no-repeat;
                background-size: contain;
                height: 100px;

                span {display: block; padding-top: $gutter*4}
                .explanation {color: $black; font-size: 1.3rem; font-family: $roman-normal-book; margin-top: calc($gutter / 3)}
            }
        }
    }






    @include media-query(tablet) {

        .zone-title {
            background-size: 160% auto;
        }
        .description .handcuffs {
            background-size: 15%;
            .description {margin-left: $gutter*3.5}
        }
        .cols--equal-heights{
            .col-20 {width: 20%;}
            .col-10 {width: 15%;}
            .col-70 {width: 65%}
        }
    }
    @include media-query(phone) {
        .zone-title {
            background-size: cover;
            .container-title {
                width: 75%;
            }
            h1 {
                font-size: 3rem;
            }
        }

        .col {
            width: 100%;
        }
        .description h4 {margin-bottom: $gutter;}
        .col {margin-bottom: $gutter; margin-top: $gutter;}
        .col.gutter-right {margin-right: 0}
        .circle-numbers {
            .col-05 {width: 10% !important; float: left}
            .col-95 {width: 90% !important; float: left}
        }
        .description .handcuffs {
            margin-left: 0;
            margin-right: 0;
        }
        .gutter-top.circle-numbers {
            margin-top: 0;
            .col {margin-top: 0}
        }
        .contentImg.wide.gutter-top {margin-bottom: $gutter;}
        .col.col-30.align-right {
            margin-top: 0;
            .large-number.gutter-top:first-of-type {margin-top: 0}
        }
        h3.gutter-top__double {margin-top: $gutter*3}
        .demolitions.contentImg.wide.gutter-top {margin-bottom: 0;margin-top: 0; padding-top: 0}
        .cols--equal-heights.gutter-top {
            margin-top: 0;
            .col {margin-top: 0; margin-bottom: 0;}
            .piechart {background-position: center center;}
            .large-number.centered {padding-top: 0; text-align: center;}
            .gutter-left.centered {margin-left: 0; padding-top: 0; text-align: center;}
        }
        .payments-numbers {
            margin-top: 0;
            .col:first-of-type {margin-top: 0;}
        }
        .prisoners-list .col:first-of-type {margin-top: 0}
        .families {margin-top: $gutter*4}
        .notes {margin-top: $gutter*2;}
    }
}
</style>